/**
 * Customer Analytics Hook
 *
 * Custom React Query hook for fetching customer portfolio data.
 * Provides pagination, filtering, and customer segmentation.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { SalesAnalyticsService } from "../services/sales-analytics";
import type { CustomerAnalytics } from "@/shared/types/sales-analytics";

/**
 * Helper hook to get current period in YYYY-MM format
 */
export function useCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed

  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Hook to fetch customer analytics for a salesperson
 *
 * @param params - Salesperson ID and optional filters
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading } = useCustomerAnalytics({
 *   salespersonId: "SP-001",
 *   period: "2025-09",
 *   limit: 20,
 *   offset: 0
 * });
 */
export function useCustomerAnalytics(
  params: {
    salespersonId: string | undefined;
    period?: string;
    limit?: number;
    offset?: number;
  },
  options?: Omit<UseQueryOptions<CustomerAnalytics>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new SalesAnalyticsService(api);

  return useQuery<CustomerAnalytics>({
    queryKey: ['customerAnalytics', params],
    queryFn: () => {
      if (!params.salespersonId) {
        throw new Error("Salesperson ID is required");
      }
      return service.getCustomerAnalytics({
        salespersonId: params.salespersonId,
        period: params.period,
        limit: params.limit,
        offset: params.offset
      });
    },
    enabled: !!params.salespersonId && (options?.enabled !== false),
    ...options
  });
}

/**
 * Helper hook to filter customers by segment
 */
export function useCustomerSegmentation(data?: CustomerAnalytics) {
  if (!data) {
    return {
      newCustomers: [],
      churnedCustomers: [],
      highImpactCustomers: [],
      activeCustomers: []
    };
  }

  const newCustomers = data.customers.filter(c => c.flags.isNew);
  const churnedCustomers = data.customers.filter(c => c.flags.isChurned);
  const highImpactCustomers = data.customers.filter(c => c.flags.isHighImpact);
  const activeCustomers = data.customers.filter(c => !c.flags.isChurned);

  return {
    newCustomers,
    churnedCustomers,
    highImpactCustomers,
    activeCustomers
  };
}

/**
 * Helper hook to calculate customer metrics
 */
export function useCustomerMetrics(data?: CustomerAnalytics) {
  if (!data) {
    return {
      retentionRate: 0,
      acquisitionRate: 0,
      churnRate: 0,
      highImpactConcentration: 0
    };
  }

  const { totalCustomers, newCustomers, churnedCustomers } = data;
  const activeCustomers = totalCustomers - churnedCustomers;
  const highImpactCount = data.customers.filter(c => c.flags.isHighImpact).length;

  return {
    retentionRate: totalCustomers > 0 ? (activeCustomers / totalCustomers) * 100 : 0,
    acquisitionRate: totalCustomers > 0 ? (newCustomers / totalCustomers) * 100 : 0,
    churnRate: totalCustomers > 0 ? (churnedCustomers / totalCustomers) * 100 : 0,
    highImpactConcentration: totalCustomers > 0 ? (highImpactCount / totalCustomers) * 100 : 0
  };
}
